import { postApi } from "@circle-react/api/postApi";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { afterSaveRedirectPath } from "@circle-react/helpers/postHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Post } from "@circle-react/types";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { isBasicPost } from "@/react/helpers/postHelpers";
import {
  isCardViewSpace,
  isListViewSpace,
  canManageSpace,
} from "@/react/helpers/spaceHelpers";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";

export const usePostDropdownActions = ({
  onPostDestroy,
}: {
  onPostDestroy?: (data: any) => void;
}) => {
  const { data: space, refetch } = useCurrentSpaceContext();
  const { post, showShareOnWebOptions } = useCurrentPostHelpers();
  const { usedIn } = useCurrentPostContext();
  const navigate = useNavigate();

  const [shouldShowDuplicatePostModal, toggleDuplicatePostModal] =
    useBoolean(false);
  const [shouldShowConfirmationModal, toggleConfirmationModal] =
    useBoolean(false);
  const [shouldShowSetThumbnailModal, toggleSetThumbnailModal] =
    useBoolean(false);

  const isPostBasicPost = isBasicPost(post);
  const isSmScreen = useSmScreenMediaQuery();
  const { success, error } = useToast();

  const shouldShowPinToSidebar =
    isPostBasicPost &&
    Boolean(!space?.hide_right_sidebar) &&
    !isCardViewSpace(space);

  const isPostCardOrListView = isCardViewSpace(space) || isListViewSpace(space);
  const isPostDetailsPage = useRouteHelpers().isPostDetailPage;
  const isCurrentPostPinnedAtTopOfSpace = post.pinned_at_top_of_space;
  const isEligibleForSharing =
    !isPostDetailsPage &&
    !isCurrentPostPinnedAtTopOfSpace &&
    isPostCardOrListView;
  const isRenderedOnSmallScreensOrFullPostModal =
    isSmScreen || usedIn === "full-post-modal";
  const shouldShowShareOnWebOptions = isRenderedOnSmallScreensOrFullPostModal
    ? showShareOnWebOptions(space)
    : isEligibleForSharing && showShareOnWebOptions(space);
  const shouldShowSetThumbnailOptionForPosts =
    isCardViewSpace(space) &&
    !isCurrentPostPinnedAtTopOfSpace &&
    canManageSpace(space);

  const deleteMutation = useMutation({
    mutationFn: () => postApi.destroy({ post }),
    onSuccess: () => {
      onPostDestroy?.(post.id);
      success(t("post_dropdown_actions.delete_successful"));
      void refetch();
    },
    onError: () => error(t("post_dropdown_actions.delete_failed")),
  });

  const redirectToPost = (post: Post) =>
    void navigate({ to: afterSaveRedirectPath({ post }) });

  return {
    shouldShowPinToSidebar,
    isPostCardOrListView,
    isPostDetailsPage,
    isCurrentPostPinnedAtTopOfSpace,
    isEligibleForSharing,
    isRenderedOnSmallScreensOrFullPostModal,
    shouldShowShareOnWebOptions,
    shouldShowSetThumbnailOptionForPosts,
    shouldShowDuplicatePostModal,
    toggleDuplicatePostModal,
    shouldShowConfirmationModal,
    toggleConfirmationModal,
    deleteMutation,
    redirectToPost,
    shouldShowSetThumbnailModal,
    toggleSetThumbnailModal,
  };
};
